import { AddFeatureMultiplierActionHandler, CollectFeatureMultiplierActionHandler, type ReplayPlugin } from "@slot/client-engine";
import type { GameFeature } from "@slot/game-shell";
import type { Log, PresentationTaskRunner } from "@slot/presentation";
import type { SavedTotalTuning } from "../../tuning/savedTotalTuning";
import { SavedTotalPresenter } from "./SavedTotalPresenter";
import type { SavedTotalView } from "./SavedTotalView";

export interface SavedTotalFeatureDeps {
  /** The panel; null → the session keeps its total unseen and every battle's top label slides in. */
  view: SavedTotalView | null;
  runner: PresentationTaskRunner;
  tuning: SavedTotalTuning;
  log: Log;
}

/**
 * The Super Bonus saved total: the canonical `add_feature_multiplier` and `collect_feature_multiplier` drive its panel
 * (no shell plugin presents them), and the feature's enter and exit open and close its session. Both are read at the
 * event's start: the shell presents the exit's finished panel in its feature-state step, before a title plugin's turn
 * there, and the panel must be gone by then.
 */
export const savedTotalFeature: GameFeature<SavedTotalFeatureDeps, SavedTotalPresenter, SavedTotalPresenter> = {
  createPresenter(deps) {
    return new SavedTotalPresenter(deps);
  },

  createPlugin(target): ReplayPlugin {
    return {
      name: "saved-total",
      actionHandlers: [
        new AddFeatureMultiplierActionHandler((added) => target.presentAdded(added)),
        new CollectFeatureMultiplierActionHandler((multiplierAdded, collectedSum) => target.presentCollected(multiplierAdded, collectedSum)),
      ],
      observer: {
        onEventStart: async (roundEvent) => {
          const state = roundEvent.eventContent.featureState;
          if (state?.featurePhase === "enter") target.onFeatureEnter(state.featureIdentifier);
          else if (state?.featurePhase === "exit") target.onFeatureExit();
        },
      },
    };
  },
};
